// ** React Imports
import { useEffect, useState } from 'react'

// ** Third Party Components
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Bell } from 'react-feather'

// ** Reactstrap Imports
import { Button, Badge, DropdownMenu, DropdownItem, DropdownToggle, UncontrolledDropdown } from 'reactstrap'

// ** Utils & services
import { getUserData } from '@utils'
import {
  getOperatorNotifications,
  getOperatorNotificationUnreadCount,
  markOperatorNotificationRead
} from '@services/passenger-bus/operator'

const timeAgo = (iso) => {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const s = Math.floor((Date.now() - then) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`
  return new Date(iso).toLocaleDateString('en-GB')
}

const NotificationDropdown = () => {
  const userData = getUserData()
  const isOperator = userData?.roleType === 'BUS_OPERATOR'

  const [items, setItems] = useState([])
  const [unread, setUnread] = useState(0)

  const load = async () => {
    try {
      const [list, count] = await Promise.all([
        getOperatorNotifications(),
        getOperatorNotificationUnreadCount()
      ])
      setItems(Array.isArray(list) ? list : [])
      setUnread(count?.count ?? 0)
    } catch {
      // bell is non-critical; fail quietly
    }
  }

  useEffect(() => {
    if (!isOperator) return undefined
    load()
    const t = setInterval(load, 60000)
    return () => clearInterval(t)
  }, [isOperator])

  const onRead = async (n) => {
    if (n.isRead) return
    try {
      await markOperatorNotificationRead(n.id)
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)))
      setUnread((u) => Math.max(0, u - 1))
    } catch {
      // ignore
    }
  }

  const markAll = async () => {
    const unreadItems = items.filter((x) => !x.isRead)
    if (unreadItems.length === 0) return
    await Promise.allSettled(unreadItems.map((x) => markOperatorNotificationRead(x.id)))
    setItems((prev) => prev.map((x) => ({ ...x, isRead: true })))
    setUnread(0)
  }

  // Non-operator users keep the (currently empty) bell — the operator portal is
  // the only surface wired to real notifications.
  if (!isOperator) {
    return <UncontrolledDropdown tag="li" className="dropdown-notification nav-item me-25" />
  }

  return (
    <UncontrolledDropdown tag="li" className="dropdown-notification nav-item me-25">
      <DropdownToggle tag="a" className="nav-link" href="/" onClick={(e) => e.preventDefault()} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}>
        <Bell size={21} />
        {unread > 0 && (
          <Badge pill color="danger" className="badge-up">
            {unread > 99 ? '99+' : unread}
          </Badge>
        )}
      </DropdownToggle>
      <DropdownMenu end tag="ul" className="dropdown-menu-media mt-0">
        <li className="dropdown-menu-header">
          <DropdownItem className="d-flex" tag="div" header>
            <h4 className="notification-title mb-0 me-auto">Notifications</h4>
            <Badge tag="div" color="light-primary" pill>
              {unread} New
            </Badge>
          </DropdownItem>
        </li>
        <PerfectScrollbar
          component="li"
          className="media-list scrollable-container"
          options={{ wheelPropagation: false }}
        >
          {items.length === 0 ? (
            <div className="p-2 text-center text-muted">
              <Bell size={22} className="mb-50 d-block mx-auto text-muted" />
              You&apos;re all caught up
            </div>
          ) : (
            items.map((n) => (
              <a
                key={n.id}
                className="d-flex"
                href="/"
                onClick={(e) => {
                  e.preventDefault()
                  onRead(n)
                }}
              >
                <div className="list-item d-flex align-items-start">
                  <div className="list-item-body flex-grow-1">
                    <p className="media-heading">
                      <span className={n.isRead ? '' : 'fw-bolder'}>{n.title}</span>
                    </p>
                    <small className="notification-text">{n.message}</small>
                    <small className="d-block text-muted mt-25">{timeAgo(n.createdAt)}</small>
                  </div>
                </div>
              </a>
            ))
          )}
        </PerfectScrollbar>
        <li className="dropdown-menu-footer">
          <Button color="primary" block disabled={unread === 0} onClick={markAll}>
            Mark all as read
          </Button>
        </li>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}

export default NotificationDropdown
